<template>
  <v-container class="versand-container">
    <v-tabs v-model="mainTab" bg-color="grey-darken-3" class="mb-3">
      <v-tab value="refurbish"><v-icon start>mdi-tools</v-icon>Frankreich</v-tab>
      <v-tab value="dtc"><v-icon start>mdi-package-variant-closed</v-icon>DTC</v-tab>
    </v-tabs>

    <DtcVersandPanel v-if="mainTab === 'dtc'" />

    <div v-show="mainTab === 'refurbish'">
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-truck-delivery</v-icon>
        Frankreich Versand & Ladelisten
        <v-spacer></v-spacer>
        <v-chip color="success" size="small">
          <v-icon start>mdi-package-variant</v-icon>
          {{ verfuegbarePaletten.length }} Paletten bereit
        </v-chip>
      </v-card-title>

      <!-- Tabs: Paletten | Ladelisten | Archiv | Auswertung -->
      <v-tabs v-model="activeTab" bg-color="rgba(30, 30, 40, 0.5)" color="success">
        <v-tab value="paletten">
          <v-icon start>mdi-package-variant-closed</v-icon>
          Paletten für Versand
        </v-tab>
        <v-tab value="ladelisten">
          <v-icon start>mdi-clipboard-list</v-icon>
          Ladelisten
        </v-tab>
        <v-tab value="archiv">
          <v-icon start>mdi-archive</v-icon>
          Archiv
        </v-tab>
        <v-tab value="auswertung">
          <v-icon start>mdi-chart-bar</v-icon>
          Auswertung
        </v-tab>
      </v-tabs>

      <v-divider class="card-divider"></v-divider>

      <v-tabs-window v-model="activeTab">
        <!-- TAB 1: Paletten-Auswahl -->
        <v-tabs-window-item value="paletten">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  <v-icon start>mdi-information</v-icon>
                  Wähle Paletten aus und erstelle eine Ladeliste für den Versand nach IS SUR TILLE
                </v-alert>
              </v-col>
            </v-row>

            <!-- Paletten-Tabelle mit Auswahl -->
            <v-data-table
              v-model="selectedPaletten"
              :headers="palettenHeaders"
              :items="verfuegbarePaletten"
              :loading="loading.paletten"
              item-value="id"
              show-select
              class="elevation-1"
            >
              <template v-slot:[`item.paletten_nr`]="{ item }">
                <strong class="text-blue-darken-1">{{ item.paletten_nr }}</strong>
              </template>
              <template v-slot:[`item.lieferschein`]="{ item }">
                <code v-if="item.lieferschein">{{ item.lieferschein }}</code>
                <span v-else class="text-grey">—</span>
              </template>
              <template v-slot:[`item.bestellnummer`]="{ item }">
                {{ item.bestellnummer || '—' }}
              </template>
              <template v-slot:[`item.gesamtgewicht`]="{ item }">
                <v-chip size="small" color="orange">
                  {{ parseFloat(item.gesamtgewicht).toFixed(2) }} kg
                </v-chip>
              </template>
              <template v-slot:[`item.anzahl_positionen`]="{ item }">
                <v-chip size="small" color="info">
                  {{ item.anzahl_positionen }} Artikel
                </v-chip>
              </template>
              <template v-slot:[`item.gesamtstueckzahl`]="{ item }">
                <v-chip size="small" color="success">
                  {{ item.gesamtstueckzahl }} Stück
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon="mdi-printer"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="printSinglePalette(item)"
                  title="Palette drucken"
                ></v-btn>
                <v-btn
                  icon="mdi-eye"
                  size="small"
                  variant="text"
                  color="info"
                  @click="showPaletteDetails(item.id)"
                  title="Details anzeigen"
                ></v-btn>
                <v-btn
                  icon="mdi-arrow-left"
                  size="small"
                  variant="text"
                  color="warning"
                  @click="paletteZurueckAnOffice(item.id)"
                  title="Zurück an Office"
                ></v-btn>
              </template>
            </v-data-table>

            <!-- Aktions-Buttons -->
            <v-row class="mt-4">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="neueLadeliste.verladedatum"
                  type="date"
                  label="Verladedatum"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  :disabled="selectedPaletten.length === 0"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-calendar</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-btn
                  block
                  color="success"
                  size="large"
                  prepend-icon="mdi-plus-circle"
                  :disabled="selectedPaletten.length === 0"
                  :loading="loading.creating"
                  @click="ladelisteErstellen"
                >
                  Ladeliste erstellen ({{ selectedPaletten.length }} Paletten)
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tabs-window-item>

        <!-- TAB 2: Ladelisten-Übersicht -->
        <v-tabs-window-item value="ladelisten">
          <v-card-text>
            <!-- Filter -->
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="statusFilter"
                  :items="['alle', 'entwurf', 'verschickt']"
                  label="Status"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  @update:model-value="fetchLadelisten"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-filter</v-icon>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Ladelisten-Tabelle -->
            <v-data-table
              :headers="ladelistenHeaders"
              :items="filteredLadelisten"
              :loading="loading.ladelisten"
              item-value="id"
              class="elevation-1"
            >
              <template v-slot:[`item.ladelisten_nr`]="{ item }">
                <strong class="text-blue-darken-1">{{ item.ladelisten_nr }}</strong>
              </template>
              <template v-slot:[`item.verladedatum`]="{ item }">
                {{ formatDate(item.verladedatum) }}
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="item.status === 'verschickt' ? 'success' : 'warning'"
                  size="small"
                >
                  <v-icon start size="small">
                    {{ item.status === 'verschickt' ? 'mdi-check-circle' : 'mdi-pencil' }}
                  </v-icon>
                  {{ item.status === 'verschickt' ? 'Verschickt' : 'Entwurf' }}
                </v-chip>
              </template>
              <template v-slot:[`item.anzahl_paletten`]="{ item }">
                <v-chip size="small" color="info">{{ item.gesamt_paletten }} Paletten</v-chip>
              </template>
              <template v-slot:[`item.gesamtgewicht`]="{ item }">
                <v-chip size="small" color="orange">
                  {{ parseFloat(item.gesamt_gewicht).toFixed(2) }} kg
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon="mdi-package-variant"
                  size="small"
                  variant="text"
                  color="blue-darken-1"
                  @click="showLadelistePaletten(item.id)"
                  title="Paletten anzeigen"
                ></v-btn>
                <v-btn
                  icon="mdi-file-export"
                  size="small"
                  variant="text"
                  color="orange-darken-1"
                  @click="exportLadelistenVorabliste(item.id)"
                  title="Vorabliste als CSV exportieren"
                ></v-btn>
                <v-btn
                  icon="mdi-printer"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="ladelisteDrucken(item.id)"
                  title="Ladeliste drucken"
                ></v-btn>
                <v-btn
                  v-if="item.status === 'entwurf'"
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  color="error"
                  @click="ladelisteLöschen(item.id)"
                  title="Ladeliste löschen"
                ></v-btn>
                <v-btn
                  v-if="item.status === 'entwurf'"
                  icon="mdi-send"
                  size="small"
                  variant="text"
                  color="success"
                  @click="ladelisteVerschicken(item.id)"
                  title="Als verschickt markieren"
                ></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tabs-window-item>

        <!-- TAB 3: Archiv -->
        <v-tabs-window-item value="archiv">
          <v-card-text>
            <v-alert type="success" variant="tonal" density="compact" class="mb-4">
              <v-icon start>mdi-archive-check</v-icon>
              Hier findest du alle verschickten Paletten mit der zugehörigen Ladeliste
            </v-alert>

            <!-- Archiv-Tabelle -->
            <v-data-table
              :headers="archivHeaders"
              :items="archivPaletten"
              :loading="loading.archiv"
              item-value="id"
              class="elevation-1"
            >
              <template v-slot:[`item.paletten_nr`]="{ item }">
                <strong class="text-grey-darken-2">{{ item.paletten_nr }}</strong>
              </template>
              <template v-slot:[`item.ladelisten_nr`]="{ item }">
                <v-chip
                  color="success"
                  size="small"
                  @click="openLadelisteFromArchiv(item)"
                  class="cursor-pointer"
                  title="Ladeliste öffnen"
                >
                  <v-icon start size="small">mdi-file-document</v-icon>
                  {{ item.ladelisten_nr || 'N/A' }}
                </v-chip>
              </template>
              <template v-slot:[`item.verladedatum`]="{ item }">
                {{ formatDate(item.verladedatum) }}
              </template>
              <template v-slot:[`item.anzahl_positionen`]="{ item }">
                <v-chip size="small" color="info">{{ item.anzahl_positionen }} Artikel</v-chip>
              </template>
              <template v-slot:[`item.gesamtgewicht`]="{ item }">
                {{ parseFloat(item.gesamtgewicht).toFixed(2) }} kg
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon="mdi-eye"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="showPaletteDetails(item.id)"
                  title="Details anzeigen"
                ></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tabs-window-item>

        <!-- TAB 4: Auswertung -->
        <v-tabs-window-item value="auswertung">
          <v-card-text>
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              <v-icon start>mdi-chart-bar</v-icon>
              Versandstatistiken und Auswertungen
            </v-alert>

            <!-- Filter -->
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-select
                  v-model="auswertungFilter.zeitraum"
                  :items="zeitraumOptionen"
                  label="Zeitraum"
                  variant="outlined"
                  density="comfortable"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3" v-if="auswertungFilter.zeitraum === 'custom'">
                <v-text-field
                  v-model="auswertungFilter.von"
                  label="Von"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" v-if="auswertungFilter.zeitraum === 'custom'">
                <v-text-field
                  v-model="auswertungFilter.bis"
                  label="Bis"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn color="primary" block @click="ladeAuswertung" :loading="loading.auswertung">
                  <v-icon start>mdi-refresh</v-icon>
                  Aktualisieren
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn color="success" block @click="druckeAuswertung">
                  <v-icon start>mdi-printer</v-icon>
                  Drucken
                </v-btn>
              </v-col>
            </v-row>

            <!-- Statistik-Karten: Gesamt -->
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-card color="blue-lighten-5">
                  <v-card-text>
                    <div class="text-overline">Paletten gesamt</div>
                    <div class="text-h4 font-weight-bold text-blue-darken-2">{{ auswertungDaten.gesamtPaletten }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="green-lighten-5">
                  <v-card-text>
                    <div class="text-overline">Artikel gesamt</div>
                    <div class="text-h4 font-weight-bold text-green-darken-2">{{ auswertungDaten.gesamtArtikel }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="orange-lighten-5">
                  <v-card-text>
                    <div class="text-overline">Gewicht gesamt</div>
                    <div class="text-h4 font-weight-bold text-orange-darken-2">{{ auswertungDaten.gesamtGewicht }} kg</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="purple-lighten-5">
                  <v-card-text>
                    <div class="text-overline">Ladelisten</div>
                    <div class="text-h4 font-weight-bold text-purple-darken-2">{{ auswertungDaten.gesamtLadelisten }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Statistik-Karten: Durchschnitte -->
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-card>
                  <v-card-text>
                    <div class="text-caption text-grey">⌀ Paletten/Ladeliste</div>
                    <div class="text-h5 font-weight-bold text-purple">
                      {{ auswertungDaten.durchschnittPalettenProLadeliste }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card>
                  <v-card-text>
                    <div class="text-caption text-grey">⌀ Artikel/Palette</div>
                    <div class="text-h5 font-weight-bold text-orange">
                      {{ auswertungDaten.durchschnittArtikelProPalette }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card>
                  <v-card-text>
                    <div class="text-caption text-grey">⌀ Gewicht/Palette</div>
                    <div class="text-h5 font-weight-bold text-brown">
                      {{ auswertungDaten.durchschnittGewichtProPalette }} kg
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card>
                  <v-card-text>
                    <div class="text-caption text-grey">⌀ Artikel/Ladeliste</div>
                    <div class="text-h5 font-weight-bold text-indigo">
                      {{ auswertungDaten.durchschnittArtikelProLadeliste }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Top Artikel -->
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-card>
                  <v-card-title class="bg-blue-grey-lighten-5">
                    <v-icon start color="primary">mdi-star</v-icon>
                    Top 10 Artikel
                  </v-card-title>
                  <v-card-text>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th class="text-left">Rang</th>
                          <th class="text-left">Artikel</th>
                          <th class="text-left">EAN</th>
                          <th class="text-right">Menge</th>
                          <th class="text-right">Gewicht</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(artikel, index) in auswertungDaten.topArtikel" :key="index">
                          <td>
                            <v-chip size="x-small" :color="index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'brown' : 'grey'">
                              #{{ index + 1 }}
                            </v-chip>
                          </td>
                          <td>{{ artikel.description_cmmf }}</td>
                          <td><small class="text-grey">{{ artikel.gtin_ean }}</small></td>
                          <td class="text-right font-weight-bold">{{ artikel.gesamt_menge }} Stk</td>
                          <td class="text-right">{{ artikel.gesamt_gewicht }} kg</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Empfänger-Statistik -->
              <v-col cols="12" md="6">
                <v-card>
                  <v-card-title class="bg-green-lighten-5">
                    <v-icon start color="success">mdi-domain</v-icon>
                    Top 10 Empfänger
                  </v-card-title>
                  <v-card-text>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th class="text-left">Rang</th>
                          <th class="text-left">Empfänger</th>
                          <th class="text-right">Ladelisten</th>
                          <th class="text-right">Paletten</th>
                          <th class="text-right">Artikel</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(empf, index) in auswertungDaten.topEmpfaenger" :key="index">
                          <td>
                            <v-chip size="x-small" :color="index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'brown' : 'grey'">
                              #{{ index + 1 }}
                            </v-chip>
                          </td>
                          <td class="font-weight-medium">{{ empf.empfaenger_firma }}</td>
                          <td class="text-right">{{ empf.anzahl_ladelisten }}</td>
                          <td class="text-right">{{ empf.anzahl_paletten }}</td>
                          <td class="text-right font-weight-bold">{{ empf.anzahl_artikel }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Wochentag-Analyse -->
            <v-row class="mb-4">
              <v-col cols="12">
                <v-card>
                  <v-card-title class="bg-amber-lighten-5">
                    <v-icon start color="warning">mdi-calendar-week</v-icon>
                    Versand nach Wochentag
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col v-for="tag in auswertungDaten.wochentage" :key="tag.wochentag" cols="12" md="4" lg="2">
                        <v-card variant="outlined">
                          <v-card-text class="text-center">
                            <div class="text-caption text-grey">{{ tag.wochentag_name }}</div>
                            <div class="text-h6 font-weight-bold text-primary">{{ tag.anzahl_ladelisten }}</div>
                            <div class="text-caption">Ladelisten</div>
                            <div class="text-body-2 mt-2">{{ tag.anzahl_paletten }} Paletten</div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Details-Tabelle -->
            <v-data-table
              :headers="auswertungHeaders"
              :items="auswertungDaten.details"
              :loading="loading.auswertung"
              item-value="id"
              class="elevation-1"
            >
              <template v-slot:[`item.verladedatum`]="{ item }">
                {{ formatDate(item.verladedatum) }}
              </template>
              <template v-slot:[`item.gesamtgewicht`]="{ item }">
                {{ parseFloat(item.gesamtgewicht).toFixed(2) }} kg
              </template>
            </v-data-table>
          </v-card-text>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card>

    <!-- Dialog: Fahrer-Name für Verschicken -->
    <v-dialog v-model="dialogs.verschicken" max-width="500">
      <v-card>
        <v-card-title class="bg-success text-white">
          <v-icon class="mr-2">mdi-truck</v-icon>
          Ladeliste verschicken
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="fahrerName"
            label="Name des Fahrers"
            variant="outlined"
            prepend-inner-icon="mdi-account"
            autofocus
          ></v-text-field>
          <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
            Alle Paletten auf dieser Ladeliste werden als "abgeschlossen" markiert.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialogs.verschicken = false">Abbrechen</v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="ladelisteVerschickenBestaetigen"
            :loading="loading.verschicken"
            :disabled="!fahrerName"
          >
            <v-icon start>mdi-check</v-icon>
            Verschickt
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Druckoptionen -->
    <v-dialog v-model="dialogs.printOptions" max-width="500" persistent>
      <v-card>
        <v-card-title class="bg-blue-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-printer-settings</v-icon>
          Druckoptionen
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <v-icon start>mdi-information</v-icon>
            Ladeliste {{ currentLadeliste?.ladelisten_nr }} vorbereiten
          </v-alert>

          <!-- Verladedatum manuell eingeben -->
          <v-text-field
            v-model="printOptions.verladedatum"
            label="Verladedatum"
            type="date"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            hide-details="auto"
            class="form-field mb-4"
            color="success"
          >
            <template v-slot:prepend-inner>
              <v-icon color="success" size="small">mdi-calendar</v-icon>
            </template>
          </v-text-field>

          <!-- Lieferscheine mitdrucken -->
          <v-checkbox
            v-model="printOptions.includeLieferscheine"
            label="Lieferscheine mitdrucken"
            color="success"
            hide-details
          >
            <template v-slot:label>
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="success">mdi-file-document-multiple</v-icon>
                <span>Lieferscheine mitdrucken</span>
              </div>
            </template>
          </v-checkbox>
          <div v-if="printOptions.includeLieferscheine" class="text-caption text-grey ml-8 mt-1">
            Für jede Palette wird ein separater Lieferschein gedruckt
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="dialogs.printOptions = false"
          >
            <v-icon start>mdi-close</v-icon>
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="confirmPrintOptions"
          >
            <v-icon start>mdi-printer</v-icon>
            Weiter zum Druck
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Ladeliste Druckvorschau -->
    <v-dialog v-model="dialogs.drucken" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-file-document</v-icon>
          Ladeliste {{ currentLadeliste?.ladelisten_nr }}
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-printer"
            variant="text"
            color="white"
            @click="printLadeliste"
          ></v-btn>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            @click="dialogs.drucken = false"
          ></v-btn>
        </v-card-title>
        <v-card-text id="ladeliste-print-area" class="pa-6">
          <!-- Ladeliste-Template (wird weiter unten definiert) -->
          <div v-if="currentLadeliste" class="ladeliste-dokument">
            <!-- Kopfzeile -->
            <div class="text-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.18 94.77" style="width: 200px; height: auto; display: block; margin: 0 auto 16px;">
                <path fill="#2f5e30" d="m.04,8.52h26.13c18.64,0,28.04,11.38,28.04,24.96,0,15.93-11.67,26.64-28.19,26.64H.04V8.52Zm16.74,40h7.27c8.29,0,12.48-4.99,12.48-14.68,0-7.93-3.3-13.73-12.48-13.8h-7.27v28.48Z"/>
                <path fill="#2f5e30" d="m61.25,8.52h16.74v18.64h18.64V8.52h16.81v51.6h-16.81v-21.07h-18.64v21.07h-16.74V8.52Z"/>
                <path fill="#2f5e30" d="m137.51,44.56c1.1,3.45,3.74,6.02,10.5,6.02,5.95,0,7.78-1.84,7.78-4.41,0-2.72-1.61-4.33-11.3-6.02-19.09-3.3-21.8-9.84-21.8-16.66,0-10.2,9.61-16.15,23.71-16.15,17.18,0,24,7.93,24.66,15.56h-16.29c-.59-2.28-1.98-4.92-8.44-4.92-4.55,0-6.68,1.47-6.68,3.89s2.13,3.52,10.2,4.99c19.45,3.45,23.27,9.62,23.27,17.4,0,9.84-8.29,16.95-25.84,16.95s-25.03-7.34-26.57-16.66h16.81Z"/>
                <g>
                  <path fill="#f4980c" d="m0,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76H1.31v5.91H0v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76H1.31v5.52h5.14Z"/>
                  <path fill="#f4980c" d="m13.92,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                  <path fill="#f4980c" d="m26.9,80.73h5.72c2.33,0,3.9,1.64,3.9,4.11s-1.57,4.12-3.9,4.12h-4.41v5.52h-1.31v-13.74Zm5.72,7.07c1.55,0,2.6-1.19,2.6-2.96s-1.05-2.95-2.6-2.95h-4.41v5.91h4.41Z"/>
                  <path fill="#f4980c" d="m42.73,80.73h1.31l5.56,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.55-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/>
                  <path fill="#f4980c" d="m52.37,80.73h1.31v13.74h-1.31v-13.74Z"/>
                  <path fill="#f4980c" d="m57.58,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/>
                  <path fill="#f4980c" d="m76.72,87.59c0-4.29,2.54-7.16,6.34-7.16s6.35,2.87,6.35,7.16-2.55,7.18-6.35,7.18-6.34-2.88-6.34-7.18Zm11.38,0c0-3.59-2.03-6-5.04-6s-5.03,2.41-5.03,6,2.02,6.02,5.03,6.02,5.04-2.42,5.04-6.02Z"/>
                  <path fill="#f4980c" d="m92.82,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/>
                  <path fill="#f4980c" d="m112.47,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/>
                  <path fill="#f4980c" d="m126.39,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                  <path fill="#f4980c" d="m139.37,80.73h1.27l7.95,11.59v-11.59h1.27v13.74h-1.27l-7.95-11.59v11.59h-1.27v-13.74Z"/>
                  <path fill="#f4980c" d="m153.76,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                  <path fill="#f4980c" d="m165.61,80.73h1.28l3.26,11.64,3.27-11.64h1.28l3.26,11.64,3.27-11.64h1.28l-3.89,13.74h-1.31l-3.24-11.43-3.26,11.43h-1.31l-3.88-13.74Z"/>
                  <path fill="#f4980c" d="m191.01,80.73h1.64l4.96,12.01,4.97-12.01h1.64v13.74h-1.21v-11.59l-4.8,11.59h-1.19l-4.8-11.59v11.59h-1.21v-13.74Z"/>
                  <path fill="#f4980c" d="m208.13,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                  <path fill="#f4980c" d="m225.55,80.73h1.31l5.55,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.56-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/>
                  <path fill="#f4980c" d="m235.19,80.73h1.27l7.94,11.59v-11.59h1.27v13.74h-1.27l-7.94-11.59v11.59h-1.27v-13.74Z"/>
                  <path fill="#f4980c" d="m248.86,90.33h1.31c0,1.96,1.61,3.29,4,3.29s4.01-1.09,4.01-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/>
                  <path fill="#f4980c" d="m268.4,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/>
                  <path fill="#f4980c" d="m282.32,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                  <path fill="#f4980c" d="m295.31,90.16v-9.43h1.31v9.43c0,2.06,1.53,3.45,3.82,3.45s3.83-1.39,3.83-3.46v-9.42h1.31v9.43c0,2.75-2.05,4.61-5.13,4.61s-5.12-1.86-5.12-4.61Z"/>
                  <path fill="#f4980c" d="m308.76,90.33h1.31c0,1.96,1.62,3.29,4.01,3.29s4-1.09,4-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/>
                  <path fill="#f4980c" d="m322.57,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/>
                </g>
                <g>
                  <path fill="#f4980c" fill-rule="evenodd" d="m269.62,34.92c4.82-.26,8.53-4.39,8.27-9.21-.26-4.82-4.39-8.53-9.21-8.27-4.82.26-8.53,4.39-8.27,9.21s4.39,8.53,9.21,8.27Z"/>
                  <path fill="#f4980c" fill-rule="evenodd" d="m294.88,28.72c-.89-16.7-15.21-29.57-31.91-28.67-7.72.41-14.6,3.71-19.71,8.77-2.29,2.27-15.27,18.22-15.27,18.22l2,1.78c2.17,5.08,1.38,11.17-2.54,15.56-4.03,4.53-10.24,5.98-15.65,4.17l9.46-10.63-8.65-7.7-9.46,10.62c-2.42-5.16-1.7-11.5,2.34-16.02,3.91-4.4,9.87-5.88,15.16-4.32l2.13,1.9,14.59-17.38c-5.23-3.44-11.53-5.33-18.25-4.98-16.7.89-29.56,15.21-28.67,31.91s15.21,29.57,31.91,28.67c8.76-.47,16.43-4.64,21.65-10.9,1.49-1.73,13.93-16.55,13.93-16.55,0,0-1.08-1.87-1.4-2.85h0s-3.28.18-3.28.18l-.37-6.94,3.28-.18h0c.23-1.07.6-2.09,1.08-3.05h0s-2.44-2.19-2.44-2.19l4.65-5.17,2.44,2.2h0c.9-.58,1.88-1.05,2.92-1.39h0s-.18-3.28-.18-3.28l6.94-.37.18,3.28h0c1.07.23,2.1.6,3.06,1.08h0s2.2-2.45,2.2-2.45l5.17,4.65-2.2,2.44h0c.58.9,1.05,1.88,1.4,2.92h0s3.28-.18,3.28-.18l.37,6.94-3.28.18h0c-.23,1.07-.6,2.09-1.08,3.05h0s2.44,2.19,2.44,2.19l-4.65,5.17-2.44-2.2h0c-.9.58-1.88,1.05-2.92,1.4h0s.18,3.28.18,3.28l-6.94.37-.18-3.28h0c-1.05-.23-3.06-1.1-3.06-1.1l-15,17.88c5.2,3.38,11.45,5.24,18.11,4.88,16.7-.89,29.56-15.21,28.67-31.91Z"/>
                </g>
              </svg>
              <h1 class="text-h4 font-weight-bold mb-1">Ladeliste</h1>
              <h2 class="text-h6 mb-1">DHS Solingen → IS sur Tille (France)</h2>
              <p class="text-subtitle-1 text-blue-darken-1 font-weight-bold">{{ currentLadeliste.ladelisten_nr }}</p>
            </div>

            <!-- Absender & Empfänger -->
            <div class="address-row mb-3">
              <div class="address-col" style="display:inline-block;width:48%;vertical-align:top;margin-right:2%;">
                <div class="info-box from-box">
                  <div class="box-header from-header">FROM / Absender</div>
                  <div class="box-content">
                    <p class="font-weight-bold mb-0">{{ currentLadeliste.absender_firma }}</p>
                    <p class="mb-0">{{ currentLadeliste.absender_strasse }}</p>
                    <p class="mb-0">{{ currentLadeliste.absender_plz_ort }}</p>
                    <p class="mb-0 text-caption"><strong>Versandstelle:</strong> {{ currentLadeliste.absender_versandstelle }}</p>
                  </div>
                </div>
              </div>
              <div class="address-col" style="display:inline-block;width:48%;vertical-align:top;">
                <div class="info-box to-box">
                  <div class="box-header to-header">TO / Empfänger</div>
                  <div class="box-content">
                    <p class="font-weight-bold mb-0">{{ currentLadeliste.empfaenger_firma }}</p>
                    <p class="mb-0">{{ currentLadeliste.empfaenger_abteilung }}</p>
                    <p class="mb-0">{{ currentLadeliste.empfaenger_strasse }}</p>
                    <p class="mb-0">{{ currentLadeliste.empfaenger_plz_ort }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Verladedatum -->
            <div class="mb-3 pa-2 bg-grey-lighten-4" style="border-left: 3px solid #1976D2;">
              <p class="text-body-2 mb-0">
                <strong>Verladedatum:</strong> {{ formatDate(currentLadeliste.verladedatum) }}
              </p>
            </div>

            <!-- Lieferscheine / Bestellungen -->
            <div class="mb-3">
              <h3 class="text-subtitle-1 mb-2 font-weight-bold">Lieferscheine / Bestellungen</h3>
              <v-table class="ladeliste-table" density="compact">
                <thead>
                  <tr class="table-header">
                    <th class="text-left">LS / Palettennummer</th>
                    <th class="text-left">Bestellung</th>
                    <th class="text-right">Gewicht (kg)</th>
                    <th class="text-center">Lagerort</th>
                    <th class="text-center">Paletten</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pos in currentLadelistePositionen" :key="pos.id" class="table-row">
                    <td class="ls-palette-cell">
                      <div class="ls-palette-main">{{ pos.lieferschein || '—' }}</div>
                      <div class="ls-palette-sub">Palettennummer: {{ pos.paletten_nr || '—' }}</div>
                      <div v-if="pos.arbeits_paletten_nr" class="ls-palette-sub">Arbeitsnummer: {{ pos.arbeits_paletten_nr }}</div>
                    </td>
                    <td>{{ pos.bestellnummer || '—' }}</td>
                    <td class="text-right">{{ parseFloat(pos.gewicht).toFixed(2) }}</td>
                    <td class="text-center">{{ pos.empfangslagerort }}</td>
                    <td class="text-center font-weight-bold">{{ pos.anzahl_paletten }}</td>
                  </tr>
                  <tr class="table-footer">
                    <td colspan="2" class="font-weight-bold">SUMME</td>
                    <td class="text-right font-weight-bold">{{ parseFloat(currentLadeliste.gesamt_gewicht).toFixed(2) }}</td>
                    <td></td>
                    <td class="text-center font-weight-bold">{{ currentLadeliste.gesamt_paletten }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <!-- Unterschriften -->
            <div class="mt-4">
              <h3 class="text-subtitle-1 mb-2 font-weight-bold">Unterschriften / Signatures</h3>
              <table style="width:100%;border-collapse:collapse;">
                <tbody>
                  <tr>
                    <td style="width:48%;vertical-align:top;padding-right:4%;">
                      <div style="border:1px solid #ddd;padding:16px;background:#fafafa;min-height:140px;">
                        <p style="font-weight:600;font-size:11px;color:#1976D2;margin:0 0 12px 0;text-transform:uppercase;">Übernahme Fahrer / Driver</p>
                        <div v-if="currentLadeliste.fahrer_name">
                          <p style="font-size:14px;font-weight:bold;margin:8px 0 4px 0;">{{ currentLadeliste.fahrer_name }}</p>
                          <p style="font-size:11px;color:#666;margin:0;">{{ formatDateTime(currentLadeliste.fahrer_unterschrift_datum) }}</p>
                        </div>
                        <div v-else style="margin-top:10px;">
                          <div style="border-bottom:3px solid #000;height:40px;margin-bottom:6px;"></div>
                          <p style="font-size:11px;color:#666;margin:0 0 16px 0;">Name</p>
                          <div style="border-bottom:3px solid #000;height:40px;margin-bottom:6px;"></div>
                          <p style="font-size:11px;color:#666;margin:0;">Datum / Date</p>
                        </div>
                      </div>
                    </td>
                    <td style="width:48%;vertical-align:top;">
                      <div style="border:1px solid #ddd;padding:16px;background:#fafafa;min-height:140px;">
                        <p style="font-weight:600;font-size:11px;color:#1976D2;margin:0 0 12px 0;text-transform:uppercase;">Versand / Shipping</p>
                        <div v-if="currentLadeliste.versand_mitarbeiter_name">
                          <p style="font-size:14px;font-weight:bold;margin:8px 0 4px 0;">{{ currentLadeliste.versand_mitarbeiter_name }}</p>
                          <p style="font-size:11px;color:#666;margin:0;">{{ formatDate(currentLadeliste.erstellt_am) }}</p>
                        </div>
                        <div v-else style="margin-top:10px;">
                          <div style="border-bottom:3px solid #000;height:40px;margin-bottom:6px;"></div>
                          <p style="font-size:11px;color:#666;margin:0 0 16px 0;">Name</p>
                          <div style="border-bottom:3px solid #000;height:40px;margin-bottom:6px;"></div>
                          <p style="font-size:11px;color:#666;margin:0;">Datum / Date</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Seitennummerierung -->
            <div class="text-center mt-4" id="page-number-ladeliste">
              <p class="text-caption text-grey">
                Seite 1 von {{ printOptions.includeLieferscheine ? currentLadelistePaletten.length + 1 : 1 }}
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Palettendetails -->
    <v-dialog v-model="dialogs.paletteDetails" max-width="800" scrollable>
      <v-card>
        <v-card-title class="bg-info text-white d-flex align-center">
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          Palettendetails: {{ selectedPaletteDetails?.paletten_nr }}
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            @click="dialogs.paletteDetails = false"
          ></v-btn>
        </v-card-title>
        <v-card-text v-if="selectedPaletteDetails" class="pa-4">
          <!-- Palette-Info -->
          <v-row class="mb-4">
            <v-col cols="6">
              <p class="text-caption text-grey">Paletten-Nr.</p>
              <p class="text-h6">{{ selectedPaletteDetails.paletten_nr }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption text-grey">Arbeits-Paletten-Nr.</p>
              <p class="text-h6">{{ selectedPaletteDetails.arbeits_paletten_nr || '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption text-grey">Lieferschein</p>
              <p class="text-h6">{{ selectedPaletteDetails.lieferschein || '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption text-grey">Bestellnummer</p>
              <p class="text-h6">{{ selectedPaletteDetails.bestellnummer || '—' }}</p>
            </v-col>
            <v-col cols="4">
              <p class="text-caption text-grey">Gesamtgewicht</p>
              <v-chip color="orange">{{ parseFloat(selectedPaletteDetails.gesamtgewicht).toFixed(2) }} kg</v-chip>
            </v-col>
            <v-col cols="4">
              <p class="text-caption text-grey">Artikel</p>
              <v-chip color="info">{{ selectedPaletteDetails.anzahl_positionen }} Artikel</v-chip>
            </v-col>
            <v-col cols="4">
              <p class="text-caption text-grey">Stückzahl</p>
              <v-chip color="success">{{ selectedPaletteDetails.gesamtstueckzahl }} Stück</v-chip>
            </v-col>
          </v-row>

          <!-- Bearbeiter-Info -->
          <v-row class="mb-4">
            <v-col cols="6">
              <p class="text-caption text-grey">Erstellt von (Refurbish)</p>
              <p class="text-subtitle-1">
                <v-icon size="small" color="primary" class="mr-1">mdi-account</v-icon>
                {{ selectedPaletteDetails.erstellt_von_name || '—' }}
              </p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption text-grey">Bearbeitet von (Office)</p>
              <p class="text-subtitle-1">
                <v-icon size="small" color="indigo" class="mr-1">mdi-account-tie</v-icon>
                {{ selectedPaletteDetails.office_bearbeiter_name || '—' }}
              </p>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Artikel-Tabelle -->
          <h3 class="text-h6 mb-3">Artikel auf der Palette</h3>
          <v-table density="compact" class="elevation-1">
            <thead>
              <tr>
                <th>Artikel-Nr.</th>
                <th>EAN</th>
                <th>Bezeichnung</th>
                <th>Menge</th>
                <th>Gewicht</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="artikel in selectedPaletteArtikel" :key="artikel.id">
                <td><code>{{ artikel.code_cmmf || '—' }}</code></td>
                <td>{{ artikel.gtin_ean || '—' }}</td>
                <td>{{ artikel.description_cmmf || '—' }}</td>
                <td><strong>{{ artikel.menge }}</strong></td>
                <td>{{ artikel.gewicht_kg ? parseFloat(artikel.gewicht_kg).toFixed(2) + ' kg' : '—' }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Ladeliste Paletten-Übersicht -->
    <v-dialog v-model="dialogs.ladelistePaletten" max-width="700" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-package-variant-closed</v-icon>
          Paletten - Ladeliste {{ currentLadeliste?.ladelisten_nr }}
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            @click="dialogs.ladelistePaletten = false"
          ></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="text-body-2 mb-4">
            Diese Ladeliste enthält <strong>{{ currentLadelistePaletten.length }} Palette(n)</strong>
          </p>

          <v-list>
            <v-list-item
              v-for="palette in currentLadelistePaletten"
              :key="palette.id"
              class="mb-2"
              border
              rounded
            >
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-package-variant</v-icon>
              </template>

              <v-list-item-title class="font-weight-bold">
                {{ palette.paletten_nr }}
              </v-list-item-title>

              <v-list-item-subtitle>
                <div class="d-flex gap-2 mt-1">
                  <v-chip size="x-small" color="grey">
                    <v-icon start size="12">mdi-file-document</v-icon>
                    {{ palette.lieferschein || 'Kein LS' }}
                  </v-chip>
                  <v-chip size="x-small" color="grey">
                    <v-icon start size="12">mdi-cart</v-icon>
                    {{ palette.bestellnummer || 'Keine Best.' }}
                  </v-chip>
                  <v-chip size="x-small" color="orange">
                    <v-icon start size="12">mdi-weight-kilogram</v-icon>
                    {{ parseFloat(palette.gesamtgewicht).toFixed(2) }} kg
                  </v-chip>
                  <v-chip size="x-small" color="blue">
                    <v-icon start size="12">mdi-package</v-icon>
                    {{ palette.gesamtstueckzahl }} Stk.
                  </v-chip>
                </div>
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-btn
                  icon="mdi-eye"
                  size="small"
                  variant="text"
                  @click="showPaletteDetails(palette.id)"
                  title="Details anzeigen"
                ></v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { API_BASE_URL } from '@/config';
import DtcVersandPanel from '@/components/Dtc/DtcVersandPanel.vue';

const toast = useToast();
const mainTab = ref('refurbish');

// DHS Logo als SVG String
const dhsLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.18 94.77"><path fill="#2f5e30" d="m.04,8.52h26.13c18.64,0,28.04,11.38,28.04,24.96,0,15.93-11.67,26.64-28.19,26.64H.04V8.52Zm16.74,40h7.27c8.29,0,12.48-4.99,12.48-14.68,0-7.93-3.3-13.73-12.48-13.8h-7.27v28.48Z"/><path fill="#2f5e30" d="m61.25,8.52h16.74v18.64h18.64V8.52h16.81v51.6h-16.81v-21.07h-18.64v21.07h-16.74V8.52Z"/><path fill="#2f5e30" d="m137.51,44.56c1.1,3.45,3.74,6.02,10.5,6.02,5.95,0,7.78-1.84,7.78-4.41,0-2.72-1.61-4.33-11.3-6.02-19.09-3.3-21.8-9.84-21.8-16.66,0-10.2,9.61-16.15,23.71-16.15,17.18,0,24,7.93,24.66,15.56h-16.29c-.59-2.28-1.98-4.92-8.44-4.92-4.55,0-6.68,1.47-6.68,3.89s2.13,3.52,10.2,4.99c19.45,3.45,23.27,9.62,23.27,17.4,0,9.84-8.29,16.95-25.84,16.95s-25.03-7.34-26.57-16.66h16.81Z"/><g><path fill="#f4980c" d="m0,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76H1.31v5.91H0v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76H1.31v5.52h5.14Z"/><path fill="#f4980c" d="m13.92,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m26.9,80.73h5.72c2.33,0,3.9,1.64,3.9,4.11s-1.57,4.12-3.9,4.12h-4.41v5.52h-1.31v-13.74Zm5.72,7.07c1.55,0,2.6-1.19,2.6-2.96s-1.05-2.95-2.6-2.95h-4.41v5.91h4.41Z"/><path fill="#f4980c" d="m42.73,80.73h1.31l5.56,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.55-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m52.37,80.73h1.31v13.74h-1.31v-13.74Z"/><path fill="#f4980c" d="m57.58,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m76.72,87.59c0-4.29,2.54-7.16,6.34-7.16s6.35,2.87,6.35,7.16-2.55,7.18-6.35,7.18-6.34-2.88-6.34-7.18Zm11.38,0c0-3.59-2.03-6-5.04-6s-5.03,2.41-5.03,6,2.02,6.02,5.03,6.02,5.04-2.42,5.04-6.02Z"/><path fill="#f4980c" d="m92.82,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m112.47,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m126.39,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m139.37,80.73h1.27l7.95,11.59v-11.59h1.27v13.74h-1.27l-7.95-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m153.76,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m165.61,80.73h1.28l3.26,11.64,3.27-11.64h1.28l3.26,11.64,3.27-11.64h1.28l-3.89,13.74h-1.31l-3.24-11.43-3.26,11.43h-1.31l-3.88-13.74Z"/><path fill="#f4980c" d="m191.01,80.73h1.64l4.96,12.01,4.97-12.01h1.64v13.74h-1.21v-11.59l-4.8,11.59h-1.19l-4.8-11.59v11.59h-1.21v-13.74Z"/><path fill="#f4980c" d="m208.13,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m225.55,80.73h1.31l5.55,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.56-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m235.19,80.73h1.27l7.94,11.59v-11.59h1.27v13.74h-1.27l-7.94-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m248.86,90.33h1.31c0,1.96,1.61,3.29,4,3.29s4.01-1.09,4.01-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m268.4,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m282.32,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m295.31,90.16v-9.43h1.31v9.43c0,2.06,1.53,3.45,3.82,3.45s3.83-1.39,3.83-3.46v-9.42h1.31v9.43c0,2.75-2.05,4.61-5.13,4.61s-5.12-1.86-5.12-4.61Z"/><path fill="#f4980c" d="m308.76,90.33h1.31c0,1.96,1.62,3.29,4.01,3.29s4-1.09,4-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m322.57,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/></g><g><path fill="#f4980c" fill-rule="evenodd" d="m269.62,34.92c4.82-.26,8.53-4.39,8.27-9.21-.26-4.82-4.39-8.53-9.21-8.27-4.82.26-8.53,4.39-8.27,9.21s4.39,8.53,9.21,8.27Z"/><path fill="#f4980c" fill-rule="evenodd" d="m294.88,28.72c-.89-16.7-15.21-29.57-31.91-28.67-7.72.41-14.6,3.71-19.71,8.77-2.29,2.27-15.27,18.22-15.27,18.22l2,1.78c2.17,5.08,1.38,11.17-2.54,15.56-4.03,4.53-10.24,5.98-15.65,4.17l9.46-10.63-8.65-7.7-9.46,10.62c-2.42-5.16-1.7-11.5,2.34-16.02,3.91-4.4,9.87-5.88,15.16-4.32l2.13,1.9,14.59-17.38c-5.23-3.44-11.53-5.33-18.25-4.98-16.7.89-29.56,15.21-28.67,31.91s15.21,29.57,31.91,28.67c8.76-.47,16.43-4.64,21.65-10.9,1.49-1.73,13.93-16.55,13.93-16.55,0,0-1.08-1.87-1.4-2.85h0s-3.28.18-3.28.18l-.37-6.94,3.28-.18h0c.23-1.07.6-2.09,1.08-3.05h0s-2.44-2.19-2.44-2.19l4.65-5.17,2.44,2.2h0c.9-.58,1.88-1.05,2.92-1.39h0s-.18-3.28-.18-3.28l6.94-.37.18,3.28h0c1.07.23,2.1.6,3.06,1.08h0s2.2-2.45,2.2-2.45l5.17,4.65-2.2,2.44h0c.58.9,1.05,1.88,1.4,2.92h0s3.28-.18,3.28-.18l.37,6.94-3.28.18h0c-.23,1.07-.6,2.09-1.08,3.05h0s2.44,2.19,2.44,2.19l-4.65,5.17-2.44-2.2h0c-.9.58-1.88,1.05-2.92,1.4h0s.18,3.28.18,3.28l-6.94.37-.18-3.28h0c-1.05-.23-3.06-1.1-3.06-1.1l-15,17.88c5.2,3.38,11.45,5.24,18.11,4.88,16.7-.89,29.56-15.21,28.67-31.91Z"/></g></svg>`;

// ========================================
// STATE
// ========================================

const activeTab = ref('paletten');
const statusFilter = ref('alle');

const verfuegbarePaletten = ref([]);
const selectedPaletten = ref([]);
const ladelisten = ref([]);
const currentLadeliste = ref(null);
const currentLadelistePositionen = ref([]);
const currentLadelistePaletten = ref([]);

const neueLadeliste = ref({
  verladedatum: new Date().toISOString().split('T')[0]
});

const fahrerName = ref('');

const loading = ref({
  paletten: false,
  ladelisten: false,
  creating: false,
  verschicken: false,
  archiv: false,
  auswertung: false
});

const dialogs = ref({
  verschicken: false,
  drucken: false,
  paletteDetails: false,
  printOptions: false,
  ladelistePaletten: false
});

const printOptions = ref({
  includeLieferscheine: false,
  verladedatum: new Date().toISOString().split('T')[0]
});

const selectedPaletteDetails = ref(null);
const selectedPaletteArtikel = ref([]);

// ========================================
// COMPUTED
// ========================================

const palettenHeaders = [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Arbeits-Nr.', key: 'arbeits_paletten_nr', sortable: true },
  { title: 'Lieferschein', key: 'lieferschein', sortable: true },
  { title: 'Bestellnummer', key: 'bestellnummer', sortable: true },
  { title: 'Gewicht', key: 'gesamtgewicht', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: false },
  { title: 'Stückzahl', key: 'gesamtstueckzahl', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false }
];

const ladelistenHeaders = [
  { title: 'Ladelisten-Nr.', key: 'ladelisten_nr', sortable: true },
  { title: 'Verladedatum', key: 'verladedatum', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Paletten', key: 'anzahl_paletten', sortable: true },
  { title: 'Gewicht', key: 'gesamtgewicht', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false }
];

const archivHeaders = [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Ladeliste', key: 'ladelisten_nr', sortable: true },
  { title: 'Verladedatum', key: 'verladedatum', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: false },
  { title: 'Gewicht', key: 'gesamtgewicht', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false }
];

const auswertungHeaders = [
  { title: 'Ladeliste', key: 'ladelisten_nr', sortable: true },
  { title: 'Verladedatum', key: 'verladedatum', sortable: true },
  { title: 'Paletten', key: 'anzahl_paletten', sortable: true },
  { title: 'Artikel', key: 'anzahl_artikel', sortable: true },
  { title: 'Gewicht (kg)', key: 'gesamtgewicht', sortable: true }
];

const zeitraumOptionen = [
  { title: 'Aktueller Monat', value: 'monat' },
  { title: 'Aktuelles Quartal', value: 'quartal' },
  { title: 'Aktuelles Jahr', value: 'jahr' },
  { title: 'Benutzerdefiniert', value: 'custom' }
];

const archivPaletten = ref([]);

const auswertungFilter = ref({
  zeitraum: 'monat',
  von: '',
  bis: ''
});

const auswertungDaten = ref({
  gesamtPaletten: 0,
  gesamtArtikel: 0,
  gesamtGewicht: 0,
  gesamtLadelisten: 0,
  durchschnittPalettenProLadeliste: 0,
  durchschnittArtikelProPalette: 0,
  durchschnittGewichtProPalette: 0,
  durchschnittArtikelProLadeliste: 0,
  topArtikel: [],
  topEmpfaenger: [],
  wochentage: [],
  details: []
});

const filteredLadelisten = computed(() => {
  if (statusFilter.value === 'alle') {
    return ladelisten.value;
  }
  return ladelisten.value.filter(l => l.status === statusFilter.value);
});

// ========================================
// METHODS - Daten laden
// ========================================

const fetchVersandPaletten = async () => {
  loading.value.paletten = true;
  try {
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_versand_paletten' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      verfuegbarePaletten.value = response.data.paletten;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Paletten');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Laden der Paletten');
  } finally {
    loading.value.paletten = false;
  }
};

const fetchLadelisten = async () => {
  loading.value.ladelisten = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladelisten',
        status: statusFilter.value === 'alle' ? null : statusFilter.value
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      ladelisten.value = response.data.ladelisten;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Ladelisten');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Laden der Ladelisten');
  } finally {
    loading.value.ladelisten = false;
  }
};

const loadLadelisteDetails = async (ladeliste_id) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladeliste_details',
        ladeliste_id: ladeliste_id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      currentLadeliste.value = response.data.ladeliste;
      currentLadelistePositionen.value = response.data.positionen;

      // Lade auch die Paletten-Liste
      const palettenResponse = await axios.post(
        `${API_BASE_URL}refurbish/index.php`,
        {
          action: 'get_ladeliste_paletten',
          ladeliste_id: ladeliste_id
        },
        { withCredentials: true }
      );

      if (palettenResponse.data.status === 'success') {
        currentLadelistePaletten.value = palettenResponse.data.paletten || [];
      }

      return true;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Ladeliste');
      return false;
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Laden der Ladeliste');
    return false;
  }
};

const openLadelisteFromArchiv = async (paletteItem) => {
  // Finde die Ladeliste ID basierend auf der Ladelisten-Nummer
  const ladeliste = ladelisten.value.find(l => l.ladelisten_nr === paletteItem.ladelisten_nr);

  if (!ladeliste) {
    toast.error('Ladeliste nicht gefunden');
    return;
  }

  // Lade Ladeliste Details und zeige Druckdialog
  const success = await loadLadelisteDetails(ladeliste.id);
  if (success) {
    dialogs.value.drucken = true;
  }
};

// ========================================
// METHODS - Ladeliste erstellen
// ========================================

const showPaletteDetails = async (item) => {
  try {
    const palette_id = typeof item === 'object' ? item.id : item;

    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: { action: 'get_palette', palette_id: palette_id },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      selectedPaletteDetails.value = response.data.palette;
      selectedPaletteArtikel.value = response.data.palette.positionen || [];
      dialogs.value.paletteDetails = true;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Details');
    }
  } catch (error) {
    console.error('Fehler:', error);
    console.error('Error response:', error.response?.data);
    toast.error('Netzwerkfehler beim Laden der Details');
  }
};

const paletteZurueckAnOffice = async (palette_id) => {
  if (!confirm('Palette wirklich zurück an Office senden zur Nachbearbeitung?')) return;

  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'update_palette_status',
        palette_id: palette_id,
        status: 'office_freigabe'
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Palette zurück an Office gesendet');
      await fetchVersandPaletten();
    } else {
      toast.error(response.data.message || 'Fehler beim Zurücksenden');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Zurücksenden');
  }
};

const ladelisteErstellen = async () => {
  if (selectedPaletten.value.length === 0) {
    toast.warning('Bitte wähle mindestens eine Palette aus');
    return;
  }

  loading.value.creating = true;
  try {
    // Nur IDs extrahieren (nicht das ganze Objekt)
    const palette_ids = selectedPaletten.value.map(p => typeof p === 'object' ? p.id : p);

    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'create_ladeliste',
        verladedatum: neueLadeliste.value.verladedatum,
        palette_ids: palette_ids
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Ladeliste erfolgreich erstellt!');
      selectedPaletten.value = [];
      neueLadeliste.value.verladedatum = new Date().toISOString().split('T')[0];

      // Beide Listen neu laden
      await fetchVersandPaletten();
      await fetchLadelisten();

      // Zur Ladelisten-Tab wechseln
      activeTab.value = 'ladelisten';
    } else {
      toast.error(response.data.message || 'Fehler beim Erstellen der Ladeliste');
      console.error('Backend-Fehler:', response.data);
    }
  } catch (error) {
    console.error('Fehler:', error);
    console.error('Response:', error.response?.data);
    const errorMsg = error.response?.data?.message || error.response?.data?.details || 'Netzwerkfehler';
    toast.error(errorMsg);
  } finally {
    loading.value.creating = false;
  }
};

// ========================================
// METHODS - Ladeliste verschicken
// ========================================

const ladelisteVerschicken = async (ladeliste_id) => {
  currentLadeliste.value = ladelisten.value.find(l => l.id === ladeliste_id);
  fahrerName.value = '';
  dialogs.value.verschicken = true;
};

const ladelisteVerschickenBestaetigen = async () => {
  loading.value.verschicken = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'mark_ladeliste_verschickt',
        ladeliste_id: currentLadeliste.value.id,
        fahrer_name: fahrerName.value
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Ladeliste als verschickt markiert!');
      dialogs.value.verschicken = false;

      // Listen neu laden
      await fetchVersandPaletten();
      await fetchLadelisten();
      await fetchArchivPaletten();
    } else {
      toast.error(response.data.message || 'Fehler');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler');
  } finally {
    loading.value.verschicken = false;
  }
};

// ========================================
// METHODS - Ladeliste drucken
// ========================================

const ladelisteDrucken = async (ladeliste_id) => {
  const success = await loadLadelisteDetails(ladeliste_id);
  if (success) {
    // Setze aktuelles Verladedatum als Standard
    printOptions.value.verladedatum = currentLadeliste.value.verladedatum || new Date().toISOString().split('T')[0];
    printOptions.value.includeLieferscheine = false;
    dialogs.value.printOptions = true;
  }
};

const confirmPrintOptions = async () => {
  // Aktualisiere Verladedatum wenn geändert
  if (printOptions.value.verladedatum !== currentLadeliste.value.verladedatum) {
    try {
      await axios.post(
        `${API_BASE_URL}refurbish/index.php`,
        {
          action: 'update_ladeliste_verladedatum',
          ladeliste_id: currentLadeliste.value.id,
          verladedatum: printOptions.value.verladedatum
        },
        { withCredentials: true }
      );
      currentLadeliste.value.verladedatum = printOptions.value.verladedatum;
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Verladedatums:', error);
      toast.error('Verladedatum konnte nicht aktualisiert werden');
    }
  }

  dialogs.value.printOptions = false;
  dialogs.value.drucken = true;
};

const printLadeliste = async () => {
  const printContent = document.getElementById('ladeliste-print-area');
  let completeHTML = printContent.innerHTML;

  // Wenn Lieferscheine mitgedruckt werden sollen, lade Paletten-Daten und füge sie hinzu
  let totalPages = 1; // Ladeliste selbst ist Seite 1

  if (printOptions.value.includeLieferscheine) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}refurbish/index.php`,
        {
          action: 'get_ladeliste_paletten',
          ladeliste_id: currentLadeliste.value.id
        },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        const paletten = response.data.paletten || [];
        totalPages += paletten.length; // Jede Palette ist eine zusätzliche Seite

        paletten.forEach((palette, index) => {
          completeHTML += `
            <div class="page-break"></div>
            <div style="border: 2px solid #000; padding: 20px; margin: 20px;">
              <div style="text-align: center; margin-bottom: 20px;">
                <div style="width: 150px; height: auto; display: block; margin: 0 auto 12px;">${dhsLogoSvg}</div>
                <h1 style="margin: 0;">Lieferschein</h1>
              </div>

              <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse;">
                <tr>
                  <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
                    <strong>Von:</strong><br>
                    ${currentLadeliste.value.absender_firma}<br>
                    ${currentLadeliste.value.absender_strasse}<br>
                    ${currentLadeliste.value.absender_plz_ort}
                  </td>
                  <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
                    <strong>An:</strong><br>
                    ${currentLadeliste.value.empfaenger_firma}<br>
                    Magasin Nord<br>
                    ${currentLadeliste.value.empfaenger_strasse}<br>
                    ${currentLadeliste.value.empfaenger_plz_ort}
                  </td>
                </tr>
              </table>

              <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; border: 1px solid #000;">
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Ladelisten-Nr.</th>
                  <td style="padding: 8px; border: 1px solid #000;">${currentLadeliste.value.ladelisten_nr || '—'}</td>
                </tr>
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Paletten-Nr.</th>
                  <td style="padding: 8px; border: 1px solid #000;">${palette.paletten_nr || '—'}</td>
                </tr>
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Lieferschein-Nr.</th>
                  <td style="padding: 8px; border: 1px solid #000;">${palette.lieferschein || '—'}</td>
                </tr>
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Bestellnummer</th>
                  <td style="padding: 8px; border: 1px solid #000;">${palette.bestellnummer || '—'}</td>
                </tr>
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Datum</th>
                  <td style="padding: 8px; border: 1px solid #000;">${formatDate(printOptions.value.verladedatum)}</td>
                </tr>
                <tr>
                  <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Gesamtgewicht</th>
                  <td style="padding: 8px; border: 1px solid #000;">${parseFloat(palette.gesamtgewicht).toFixed(2)} kg</td>
                </tr>
              </table>

              ${palette.positionen && palette.positionen.length > 0 ? `
              <h3 style="margin-top: 20px; margin-bottom: 10px;">Artikel-Liste</h3>
              <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; border: 1px solid #000; font-size: 10px;">
                <thead>
                  <tr>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 12%;">EAN</th>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 10%;">Code</th>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 48%;">Beschreibung</th>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: center; width: 10%;">Menge</th>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: right; width: 10%;">Gew./Stk</th>
                    <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: right; width: 10%;">Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  ${palette.positionen.map(pos => `
                    <tr>
                      <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.gtin_ean || '—'}</td>
                      <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.code_cmmf || '—'}</td>
                      <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.description_cmmf || pos.code_commercial || '—'}</td>
                      <td style="padding: 3px; border: 1px solid #000; text-align: center;">${pos.menge || 0}x</td>
                      <td style="padding: 3px; border: 1px solid #000; text-align: right;">${parseFloat(pos.gewicht_kg || 0).toFixed(2)}</td>
                      <td style="padding: 3px; border: 1px solid #000; text-align: right;">${parseFloat(pos.gesamt_gewicht || 0).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                  <tr style="background: #f0f0f0; font-weight: bold; font-size: 10px;">
                    <td colspan="3" style="padding: 6px; border: 1px solid #000; text-align: right;">Gesamtanzahl Artikel:</td>
                    <td style="padding: 6px; border: 1px solid #000; text-align: center;">${palette.gesamtstueckzahl || palette.positionen.reduce((sum, p) => sum + (p.menge || 0), 0)} Stück</td>
                    <td colspan="2" style="padding: 6px; border: 1px solid #000;"></td>
                  </tr>
                </tbody>
              </table>
              ` : ''}

              <div style="margin-top: 40px; border-top: 1px solid #000; padding-top: 10px;">
                <p><strong>Unterschrift Fahrer:</strong> _________________________</p>
                <p><strong>Unterschrift Empfänger:</strong> _________________________</p>
                <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 20px;"><strong>Seite ${index + 2} von ${paletten.length + 1}</strong></p>
              </div>
            </div>
          `;
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Lieferscheine:', error);
      toast.error('Fehler beim Laden der Lieferschein-Daten');
    }
  }

  // Ersetze Platzhalter für Gesamtseitenzahl
  completeHTML = completeHTML.replace('__TOTAL_PAGES__', totalPages);

  // Drucke alles zusammen
  const windowPrint = window.open('', '_blank', 'width=900,height=650');

  windowPrint.document.write(`
    <html>
      <head>
        <title>Ladeliste ${currentLadeliste.value.ladelisten_nr}${printOptions.value.includeLieferscheine ? ' mit Lieferscheinen' : ''}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .border { border: 1px solid #000; }
          .border-t-sm { border-top: 1px solid #000; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 8px; text-align: left; }
          th { background-color: #f0f0f0; }
          .ladeliste-table th,
          .ladeliste-table td { padding: 6px; font-size: 11px; vertical-align: top; }
          .ls-palette-cell { min-width: 155px; max-width: 210px; line-height: 1.25; }
          .ls-palette-main { font-weight: 600; }
          .ls-palette-sub { font-size: 10px; color: #555; }
          .text-center { text-align: center; }
          .signature-line { border-bottom: 1px solid #000; height: 40px; margin-top: 20px; }
          @media print {
            body { margin: 0; }
            .page-break { page-break-after: always; }
          }
        </style>
      </head>
      <body>
        ${completeHTML}
      </body>
    </html>
  `);

  windowPrint.document.close();
  windowPrint.focus();

  setTimeout(() => {
    windowPrint.print();
    windowPrint.close();
  }, 250);
};

const printLieferscheine = async () => {
  try {
    // Hole alle Paletten-IDs aus den Positionen
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladeliste_paletten',
        ladeliste_id: currentLadeliste.value.id
      },
      { withCredentials: true }
    );

    if (response.data.status !== 'success') {
      toast.error('Fehler beim Laden der Paletten-Daten');
      return;
    }

    const paletten = response.data.paletten || [];
    const windowPrint = window.open('', '_blank', 'width=900,height=650');
    let lieferscheineHTML = '';

    paletten.forEach((palette, index) => {
      lieferscheineHTML += `
      <div class="lieferschein-page ${index < paletten.length - 1 ? 'page-break' : ''}">
        <div style="border: 2px solid #000; padding: 20px; margin: 20px;">
          <div style="text-align: center; margin-bottom: 20px;">
            <div style="width: 150px; height: auto; display: block; margin: 0 auto 12px;">${dhsLogoSvg}</div>
            <h1 style="margin: 0;">Lieferschein</h1>
          </div>

          <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse;">
            <tr>
              <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
                <strong>Von:</strong><br>
                ${currentLadeliste.value.absender_firma}<br>
                ${currentLadeliste.value.absender_strasse}<br>
                ${currentLadeliste.value.absender_plz_ort}
              </td>
              <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
                <strong>An:</strong><br>
                ${currentLadeliste.value.empfaenger_firma}<br>
                Magasin Nord<br>
                ${currentLadeliste.value.empfaenger_strasse}<br>
                ${currentLadeliste.value.empfaenger_plz_ort}
              </td>
            </tr>
          </table>

          <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; border: 1px solid #000;">
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Ladelisten-Nr.</th>
              <td style="padding: 8px; border: 1px solid #000;">${currentLadeliste.value.ladelisten_nr || '—'}</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Paletten-Nr.</th>
              <td style="padding: 8px; border: 1px solid #000;">${palette.paletten_nr || '—'}</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Lieferschein-Nr.</th>
              <td style="padding: 8px; border: 1px solid #000;">${palette.lieferschein || '—'}</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Bestellnummer</th>
              <td style="padding: 8px; border: 1px solid #000;">${palette.bestellnummer || '—'}</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Datum</th>
              <td style="padding: 8px; border: 1px solid #000;">${formatDate(printOptions.value.verladedatum)}</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Gewicht</th>
              <td style="padding: 8px; border: 1px solid #000;">${parseFloat(palette.gesamtgewicht).toFixed(2)} kg</td>
            </tr>
            <tr>
              <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Artikel</th>
              <td style="padding: 8px; border: 1px solid #000;">${palette.anzahl_positionen} Positionen (${palette.gesamtstueckzahl} Stück)</td>
            </tr>
          </table>

          <div style="margin-top: 40px; border-top: 1px solid #000; padding-top: 10px;">
            <p><strong>Unterschrift Fahrer:</strong> _________________________</p>
            <p><strong>Unterschrift Empfänger:</strong> _________________________</p>
            <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 20px;"><strong>Seite ${index + 1} von ${paletten.length}</strong></p>
          </div>
        </div>
      </div>
    `;
    });

    windowPrint.document.write(`
      <html>
        <head>
          <title>Lieferscheine - Ladeliste ${currentLadeliste.value.ladelisten_nr}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; }
            .page-break { page-break-after: always; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          ${lieferscheineHTML}
        </body>
      </html>
    `);

    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 250);
  } catch (error) {
    console.error('Fehler beim Drucken der Lieferscheine:', error);
    toast.error('Fehler beim Laden der Lieferschein-Daten');
  }
};

const printSinglePalette = async (palette) => {
  try {
    // Lade Palette mit Positionen
    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params: {
        action: 'get_palette',
        palette_id: palette.id
      },
      withCredentials: true
    });

    if (response.data.status !== 'success') {
      toast.error('Fehler beim Laden der Palette');
      return;
    }

    const paletteData = response.data.palette;
    const positionen = paletteData.positionen || [];

    const windowPrint = window.open('', '_blank', 'width=900,height=650');

    const lieferscheinHTML = `
      <div style="border: 2px solid #000; padding: 20px; margin: 20px;">
        <div style="text-align: center; margin-bottom: 20px;">
          <div style="width: 150px; height: auto; display: block; margin: 0 auto 12px;">${dhsLogoSvg}</div>
          <h1 style="margin: 0;">Lieferschein - Palette ${palette.paletten_nr}</h1>
        </div>

        <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse;">
          <tr>
            <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
              <strong>Von:</strong><br>
              DHS GmbH<br>
              Untengönrather Str. 73<br>
              42655 Solingen
            </td>
            <td style="width: 50%; padding: 10px; border: 1px solid #ccc;">
              <strong>An:</strong><br>
              SEB<br>
              Magasin Nord<br>
              Rue du triage<br>
              21120 IS SUR TILLE
            </td>
          </tr>
        </table>

        <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; border: 1px solid #000;">
          <tr>
            <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Paletten-Nr.</th>
            <td style="padding: 8px; border: 1px solid #000;">${palette.paletten_nr}</td>
          </tr>
          <tr>
            <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Lieferschein-Nr.</th>
            <td style="padding: 8px; border: 1px solid #000;">${palette.lieferschein || '—'}</td>
          </tr>
          <tr>
            <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Bestellnummer</th>
            <td style="padding: 8px; border: 1px solid #000;">${palette.bestellnummer || '—'}</td>
          </tr>
          <tr>
            <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Datum</th>
            <td style="padding: 8px; border: 1px solid #000;">${formatDate(new Date().toISOString().split('T')[0])}</td>
          </tr>
          <tr>
            <th style="background: #f0f0f0; padding: 8px; border: 1px solid #000;">Gesamtgewicht</th>
            <td style="padding: 8px; border: 1px solid #000;">${parseFloat(palette.gesamtgewicht).toFixed(2)} kg</td>
          </tr>
        </table>

        ${positionen.length > 0 ? `
        <h3 style="margin-top: 20px; margin-bottom: 10px;">Artikel-Liste</h3>
        <table style="width: 100%; margin-bottom: 20px; border-collapse: collapse; border: 1px solid #000; font-size: 10px;">
          <thead>
            <tr>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 12%;">EAN</th>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 10%;">Code</th>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: left; width: 48%;">Beschreibung</th>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: center; width: 10%;">Menge</th>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: right; width: 10%;">Gew./Stk</th>
              <th style="background: #f0f0f0; padding: 4px; border: 1px solid #000; text-align: right; width: 10%;">Gesamt</th>
            </tr>
          </thead>
          <tbody>
            ${positionen.map(pos => `
              <tr>
                <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.gtin_ean || '—'}</td>
                <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.code_cmmf || '—'}</td>
                <td style="padding: 3px; border: 1px solid #000; font-size: 9px;">${pos.description_cmmf || pos.code_commercial || '—'}</td>
                <td style="padding: 3px; border: 1px solid #000; text-align: center;">${pos.menge || 0}x</td>
                <td style="padding: 3px; border: 1px solid #000; text-align: right;">${parseFloat(pos.gewicht_kg || 0).toFixed(2)}</td>
                <td style="padding: 3px; border: 1px solid #000; text-align: right;">${(parseFloat(pos.menge || 0) * parseFloat(pos.gewicht_kg || 0)).toFixed(2)}</td>
              </tr>
            `).join('')}
            <tr style="background: #f0f0f0; font-weight: bold; font-size: 10px;">
              <td colspan="3" style="padding: 6px; border: 1px solid #000; text-align: right;">Gesamtanzahl Artikel:</td>
              <td style="padding: 6px; border: 1px solid #000; text-align: center;">${positionen.reduce((sum, p) => sum + (p.menge || 0), 0)} Stück</td>
              <td colspan="2" style="padding: 6px; border: 1px solid #000;"></td>
            </tr>
          </tbody>
        </table>
        ` : ''}

        <div style="margin-top: 40px; border-top: 1px solid #000; padding-top: 10px;">
          <p><strong>Unterschrift Fahrer:</strong> _________________________</p>
          <p><strong>Unterschrift Empfänger:</strong> _________________________</p>
        </div>
      </div>
    `;

    windowPrint.document.write(`
      <html>
        <head>
          <title>Lieferschein - Palette ${palette.paletten_nr}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          ${lieferscheinHTML}
        </body>
      </html>
    `);

    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 250);

  } catch (error) {
    console.error('Fehler beim Drucken der Palette:', error);
    toast.error('Fehler beim Laden der Palettendaten');
  }
};

// ========================================
// METHODS - Ladeliste Paletten anzeigen
// ========================================

const showLadelistePaletten = async (ladeliste_id) => {
  try {
    // Finde die Ladeliste
    currentLadeliste.value = ladelisten.value.find(l => l.id === ladeliste_id);

    // Lade Paletten
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladeliste_paletten',
        ladeliste_id: ladeliste_id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      currentLadelistePaletten.value = response.data.paletten || [];
      dialogs.value.ladelistePaletten = true;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Paletten');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Laden der Paletten');
  }
};

// ========================================
// METHODS - Ladeliste Vorabliste CSV exportieren
// ========================================

const exportLadelistenVorabliste = async (ladeliste_id) => {
  try {
    // Lade Ladeliste Details
    const detailsResponse = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladeliste_details',
        ladeliste_id: ladeliste_id
      },
      { withCredentials: true }
    );

    if (detailsResponse.data.status !== 'success') {
      toast.error('Fehler beim Laden der Ladeliste');
      return;
    }

    // Lade Paletten mit Positionen
    const palettenResponse = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_ladeliste_paletten',
        ladeliste_id: ladeliste_id
      },
      { withCredentials: true }
    );

    if (palettenResponse.data.status !== 'success') {
      toast.error('Fehler beim Laden der Paletten');
      return;
    }

    const paletten = palettenResponse.data.paletten || [];
    const ladeliste = detailsResponse.data.ladeliste;

    // Aggregiere Positionen nach CMMF
    const aggregiert = {};

    paletten.forEach(palette => {
      if (palette.positionen && Array.isArray(palette.positionen)) {
        palette.positionen.forEach(pos => {
          const key = pos.code_cmmf || 'UNKNOWN';

          if (!aggregiert[key]) {
            aggregiert[key] = {
              code_cmmf: pos.code_cmmf || '—',
              code_commercial: pos.code_commercial || '—',
              description_cmmf: pos.description_cmmf || pos.code_commercial || '—',
              menge: 0
            };
          }

          aggregiert[key].menge += parseInt(pos.menge) || 0;
        });
      }
    });

    // Erstelle CSV-Zeilen
    const csvLines = [];

    // Header (mit Semikolon Trennzeichen für deutsches Excel)
    csvLines.push('CMMF;SKU;Beschreibung;Menge');

    // Daten sortiert nach CMMF
    Object.keys(aggregiert)
      .sort()
      .forEach(key => {
        const item = aggregiert[key];
        const cmmf = item.code_cmmf.replace(/"/g, '""');
        const sku = item.code_commercial.replace(/"/g, '""');
        const desc = item.description_cmmf.replace(/"/g, '""');
        const menge = item.menge;

        csvLines.push(`${cmmf};${sku};${desc};${menge}`);
      });

    // Erstelle Blob und Download mit UTF-8 BOM für Excel Kompatibilität
    const csvContent = '\uFEFF' + csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Vorabliste_${ladeliste.ladelisten_nr}_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`CSV exportiert: ${filename}`);
  } catch (error) {
    console.error('Fehler beim Export:', error);
    toast.error('Fehler beim Exportieren der Vorabliste');
  }
};

// ========================================
// UTILS
// ========================================

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE');
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE');
};

// ========================================
// LIFECYCLE
// ========================================

onMounted(() => {
  fetchVersandPaletten();
  fetchLadelisten();
  fetchArchivPaletten();
});

// ========================================
// METHODS - Archiv laden
// ========================================

const fetchArchivPaletten = async () => {
  loading.value.archiv = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'get_archiv_paletten'
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      archivPaletten.value = response.data.paletten || [];
    } else {
      console.error('Archiv Fehler:', response.data.message);
      toast.error(response.data.message || 'Fehler beim Laden des Archivs');
    }
  } catch (error) {
    console.error('Fehler beim Laden der Archiv-Paletten:', error);
    console.error('Error response:', error.response?.data);
    toast.error('Archiv konnte nicht geladen werden');
  } finally {
    loading.value.archiv = false;
  }
};

// ========================================
// METHODS - Ladeliste löschen
// ========================================

const ladelisteLöschen = async (ladeliste_id) => {
  if (!confirm('Möchtest du diese Ladeliste wirklich löschen? Die Paletten werden wieder für den Versand freigegeben.')) {
    return;
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}refurbish/index.php`,
      {
        action: 'delete_ladeliste',
        ladeliste_id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success('Ladeliste gelöscht und Paletten wieder freigegeben');
      await fetchVersandPaletten();
      await fetchLadelisten();
    } else {
      toast.error(response.data.message || 'Fehler beim Löschen');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Löschen');
  }
};

// ========================================
// METHODS - Auswertung
// ========================================

const ladeAuswertung = async () => {
  loading.value.auswertung = true;
  try {
    const params = {
      action: 'get_versand_auswertung',
      zeitraum: auswertungFilter.value.zeitraum
    };

    if (auswertungFilter.value.zeitraum === 'custom') {
      params.von = auswertungFilter.value.von;
      params.bis = auswertungFilter.value.bis;
    }

    const response = await axios.get(`${API_BASE_URL}refurbish/index.php`, {
      params,
      withCredentials: true
    });

    if (response.data.status === 'success') {
      auswertungDaten.value = response.data.auswertung;
    } else {
      toast.error(response.data.message || 'Fehler beim Laden der Auswertung');
    }
  } catch (error) {
    console.error('Fehler:', error);
    toast.error('Netzwerkfehler beim Laden der Auswertung');
  } finally {
    loading.value.auswertung = false;
  }
};

const druckeAuswertung = () => {
  const windowPrint = window.open('', '_blank', 'width=900,height=650');

  let zeitraumText = '';
  switch (auswertungFilter.value.zeitraum) {
    case 'monat': zeitraumText = 'Aktueller Monat'; break;
    case 'quartal': zeitraumText = 'Aktuelles Quartal'; break;
    case 'jahr': zeitraumText = 'Aktuelles Jahr'; break;
    case 'custom': zeitraumText = `${auswertungFilter.value.von} bis ${auswertungFilter.value.bis}`; break;
  }

  windowPrint.document.write(`
    <html>
      <head>
        <title>Versand-Auswertung</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { text-align: center; margin-bottom: 10px; }
          h2 { margin-top: 30px; margin-bottom: 15px; font-size: 18px; border-bottom: 2px solid #333; padding-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 30px; }
          .stats { display: flex; justify-content: space-around; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
          .stat-box { text-align: center; padding: 12px; border: 2px solid #ddd; border-radius: 8px; min-width: 140px; }
          .stat-label { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .stat-value { font-size: 28px; font-weight: bold; color: #1976D2; }
          .stats-avg { display: flex; justify-content: space-around; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
          .stat-box-avg { text-align: center; padding: 10px; border: 1px solid #ccc; border-radius: 5px; min-width: 120px; background: #f9f9f9; }
          .stat-label-avg { font-size: 10px; color: #666; margin-bottom: 5px; }
          .stat-value-avg { font-size: 20px; font-weight: bold; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 20px; }
          th, td { border: 1px solid #000; padding: 6px; text-align: left; font-size: 11px; }
          th { background-color: #f0f0f0; font-weight: bold; }
          td.text-right { text-align: right; }
          .rank-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; color: white; font-size: 10px; font-weight: bold; }
          .rank-1 { background: gold; color: #333; }
          .rank-2 { background: silver; color: #333; }
          .rank-3 { background: #CD7F32; color: white; }
          .rank-other { background: #999; color: white; }
          .two-column { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #999; page-break-after: avoid; }
          @media print {
            body { margin: 0; }
            .page-break { page-break-before: always; }
          }
        </style>
      </head>
      <body>
        <h1>Versand-Auswertung</h1>
        <div class="subtitle">Zeitraum: ${zeitraumText}</div>

        <h2>Gesamtstatistik</h2>
        <div class="stats">
          <div class="stat-box">
            <div class="stat-label">Paletten gesamt</div>
            <div class="stat-value">${auswertungDaten.value.gesamtPaletten}</div>
          </div>
          <div class="stat-box">
            <div class="stat-label">Artikel gesamt</div>
            <div class="stat-value">${auswertungDaten.value.gesamtArtikel}</div>
          </div>
          <div class="stat-box">
            <div class="stat-label">Gewicht gesamt</div>
            <div class="stat-value">${auswertungDaten.value.gesamtGewicht} kg</div>
          </div>
          <div class="stat-box">
            <div class="stat-label">Ladelisten</div>
            <div class="stat-value">${auswertungDaten.value.gesamtLadelisten}</div>
          </div>
        </div>

        <h2>Durchschnittswerte</h2>
        <div class="stats-avg">
          <div class="stat-box-avg">
            <div class="stat-label-avg">⌀ Paletten/Ladeliste</div>
            <div class="stat-value-avg">${auswertungDaten.value.durchschnittPalettenProLadeliste}</div>
          </div>
          <div class="stat-box-avg">
            <div class="stat-label-avg">⌀ Artikel/Palette</div>
            <div class="stat-value-avg">${auswertungDaten.value.durchschnittArtikelProPalette}</div>
          </div>
          <div class="stat-box-avg">
            <div class="stat-label-avg">⌀ Gewicht/Palette</div>
            <div class="stat-value-avg">${auswertungDaten.value.durchschnittGewichtProPalette} kg</div>
          </div>
          <div class="stat-box-avg">
            <div class="stat-label-avg">⌀ Artikel/Ladeliste</div>
            <div class="stat-value-avg">${auswertungDaten.value.durchschnittArtikelProLadeliste}</div>
          </div>
        </div>

        <div class="two-column">
          <div>
            <h2>Top 10 Artikel</h2>
            <table>
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Artikel</th>
                  <th>EAN</th>
                  <th class="text-right">Menge</th>
                  <th class="text-right">Gewicht</th>
                </tr>
              </thead>
              <tbody>
                ${auswertungDaten.value.topArtikel.map((artikel, index) => `
                  <tr>
                    <td>
                      <span class="rank-badge ${index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : index === 2 ? 'rank-3' : 'rank-other'}">#${index + 1}</span>
                    </td>
                    <td>${artikel.description_cmmf || '—'}</td>
                    <td><small>${artikel.gtin_ean || '—'}</small></td>
                    <td class="text-right"><strong>${artikel.gesamt_menge} Stk</strong></td>
                    <td class="text-right">${artikel.gesamt_gewicht} kg</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div>
            <h2>Top 10 Empfänger</h2>
            <table>
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Empfänger</th>
                  <th class="text-right">LL</th>
                  <th class="text-right">Pal.</th>
                  <th class="text-right">Art.</th>
                </tr>
              </thead>
              <tbody>
                ${auswertungDaten.value.topEmpfaenger.map((empf, index) => `
                  <tr>
                    <td>
                      <span class="rank-badge ${index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : index === 2 ? 'rank-3' : 'rank-other'}">#${index + 1}</span>
                    </td>
                    <td>${empf.empfaenger_firma || '—'}</td>
                    <td class="text-right">${empf.anzahl_ladelisten}</td>
                    <td class="text-right">${empf.anzahl_paletten}</td>
                    <td class="text-right"><strong>${empf.anzahl_artikel}</strong></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <h2>Versand nach Wochentag</h2>
        <table style="width: 70%; margin: 0 auto;">
          <thead>
            <tr>
              <th>Wochentag</th>
              <th class="text-right">Ladelisten</th>
              <th class="text-right">Paletten</th>
            </tr>
          </thead>
          <tbody>
            ${auswertungDaten.value.wochentage.map(tag => `
              <tr>
                <td>${tag.wochentag_name}</td>
                <td class="text-right"><strong>${tag.anzahl_ladelisten}</strong></td>
                <td class="text-right">${tag.anzahl_paletten}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="page-break"></div>

        <h2>Ladelisten Details</h2>
        <table>
          <thead>
            <tr>
              <th>Ladelisten-Nr.</th>
              <th>Verladedatum</th>
              <th class="text-right">Paletten</th>
              <th class="text-right">Artikel</th>
              <th class="text-right">Gewicht (kg)</th>
            </tr>
          </thead>
          <tbody>
            ${auswertungDaten.value.details.map(detail => `
              <tr>
                <td>${detail.ladelisten_nr || '—'}</td>
                <td>${formatDate(detail.verladedatum)}</td>
                <td class="text-right">${detail.anzahl_paletten}</td>
                <td class="text-right">${detail.anzahl_artikel}</td>
                <td class="text-right">${parseFloat(detail.gesamtgewicht).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Erstellt am: ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')} | DHS GmbH - Refurbish Versand
        </div>
      </body>
    </html>
  `);

  windowPrint.document.close();
  windowPrint.focus();

  setTimeout(() => {
    windowPrint.print();
    windowPrint.close();
  }, 250);
};

</script>

<style scoped>
.versand-container {
  max-width: 1400px;
  margin: 0 auto;
}

.ladeliste-dokument {
  font-family: 'Segoe UI', Arial, sans-serif;
  line-height: 1.3;
  color: #333;
  font-size: 13px;
}

/* SVG Logo Styling */
.ladeliste-dokument svg {
  max-width: 200px;
  height: auto !important;
}

@media print {
  .ladeliste-dokument svg {
    max-width: 150px;
    height: auto !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

/* Info-Boxen für Absender/Empfänger */
.info-box {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  height: 100%;
}

.box-header {
  padding: 6px 12px;
  font-weight: bold;
  color: white;
  font-size: 11px;
  letter-spacing: 0.3px;
}

.from-header {
  background: #1976D2;
}

.to-header {
  background: #388E3C;
}

.box-content {
  padding: 8px 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  font-size: 12px;
  line-height: 1.3;
}

.box-content p {
  margin-bottom: 2px;
}

/* Tabelle */
.ladeliste-table {
  border: 2px solid #1976D2;
  border-radius: 4px;
  overflow: hidden;
}

.ladeliste-table .table-header {
  background: #1976D2;
  color: white;
}

.ladeliste-table .table-header th {
  font-weight: bold !important;
  padding: 8px 10px !important;
  border-bottom: none !important;
  color: white !important;
  font-size: 12px !important;
}

.ladeliste-table .table-row td {
  padding: 6px 10px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 12px !important;
}

.ls-palette-cell {
  min-width: 155px;
  max-width: 220px;
}

.ls-palette-main {
  font-weight: 700;
}

.ls-palette-sub {
  font-size: 11px;
  color: #616161;
}

.ladeliste-table .table-row:hover {
  background-color: #f5f5f5;
}

.ladeliste-table .table-footer {
  background-color: #1976D2 !important;
  color: white !important;
  border-top: 3px solid #0D47A1;
}

.ladeliste-table .table-footer td {
  padding: 10px !important;
  font-size: 14px !important;
  color: white !important;
  font-weight: bold !important;
}

/* Unterschriften */
.signature-box {
  border: 2px solid #1976D2;
  border-radius: 4px;
  padding: 12px;
  background: #FAFAFA;
  min-height: 120px;
}

.signature-label {
  font-weight: bold;
  font-size: 11px;
  color: #1976D2;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.signature-content {
  margin-top: 8px;
}

.signature-name {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
}

.signature-date {
  font-size: 11px;
  color: #666;
}

.signature-placeholder {
  margin-top: 8px;
}

.signature-line {
  border-bottom: 2px solid #333;
  height: 30px;
  position: relative;
}

.signature-hint {
  font-size: 10px;
  color: #999;
  margin-top: 2px;
  margin-bottom: 0;
}

/* Custom Flexbox-Layout für Print */
.address-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.address-col {
  flex: 1;
  min-width: 0;
}

.signature-row {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.signature-col {
  flex: 1;
  min-width: 0;
}

.versand-container {
  padding: 20px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

@media print {
  .ladeliste-dokument {
    font-size: 11px;
  }

  /* SVG Logo im Druck */
  svg {
    width: auto !important;
    height: 50px !important;
    display: block !important;
  }

  .box-content {
    font-size: 10px;
  }

  .ladeliste-table .table-header th {
    font-size: 10px !important;
    padding: 6px 8px !important;
  }

  .ladeliste-table .table-row td {
    font-size: 10px !important;
    padding: 4px 8px !important;
  }

  .ls-palette-cell {
    min-width: 140px;
    max-width: 190px;
  }

  .ls-palette-sub {
    font-size: 9px;
  }

  .ladeliste-table .table-footer td {
    font-size: 12px !important;
    padding: 8px !important;
  }

  .signature-box {
    min-height: 100px;
    padding: 10px;
  }

  .signature-line {
    height: 25px;
  }

  /* Unterschriften-Linien im Druck anzeigen */
  .signature-placeholder {
    display: block !important;
  }

  .signature-line {
    border-bottom: 3px solid #000 !important;
    height: 40px !important;
    margin-top: 25px !important;
    margin-bottom: 6px !important;
    display: block !important;
    width: 100% !important;
  }

  .signature-line span {
    display: block !important;
    height: 100% !important;
  }

  .signature-hint {
    display: block !important;
    font-size: 10px !important;
    color: #000 !important;
    margin-bottom: 8px !important;
  }

  /* Tabelle im Druck optimieren */
  .ladeliste-table {
    border: 2px solid #000 !important;
  }

  .ladeliste-table th,
  .ladeliste-table td {
    border: 1px solid #333 !important;
    padding: 6px 8px !important;
  }

  .table-header {
    background: #1976D2 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .table-footer {
    background: #1976D2 !important;
    color: white !important;
    font-weight: bold !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* FLEXBOX für nebeneinander im Druck */
  .address-row,
  .signature-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 12px !important;
    page-break-inside: avoid !important;
  }

  .address-col,
  .signature-col {
    flex: 1 1 48% !important;
    max-width: 48% !important;
    min-width: 48% !important;
    box-sizing: border-box !important;
  }

  .v-btn, .v-card-title button {
    display: none !important;
  }

  /* Seitenumbruch vermeiden */
  .info-box,
  .signature-box,
  .ladeliste-table {
    page-break-inside: avoid !important;
  }
}
</style>
